import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import * as styles from "./geofence-detail-assets.module.scss";
import VelavuInput from "../../../../elements/velavu-input";
import IconSearch from "../../../../dynamicicons/icon-search";
import VelavuIconButton from "../../../../elements/velavu-icon-button";
import IconClose from "../../../../dynamicicons/icon-close";
import IconFilterList from "../../../../dynamicicons/icon-filter-list";
import Divider from "../../../../elements/divider";
import AssetListFilter from "../asset-list/asset-list-filter";
import AssetListItem from "../asset-list/asset-listitem";
import { isDevicePaired, VelavuAPI, VelavuDevice } from "velavu-js-api";
import { useCancellableEffect } from "../../../../helper/hook-helper";
import useAssetFiltering from "../../../../helper/assets-filter-hook";
import whitelabel from "../../../../whitelabel";

export interface GeofenceDetailAssetsProps {
	geofenceID: string;
	onSelectDevice?: (deviceID: string) => void;
	onHoverDevice?: (deviceID: string | undefined) => void;
}

export default function GeofenceDetailAssets(props: GeofenceDetailAssetsProps) {
	const { t } = useTranslation();
	const [geofenceDevices, setGeofenceDevices] = useState<
		undefined | VelavuDevice[]
	>(undefined);
	useCancellableEffect(
		(addPromise) => {
			addPromise(
				VelavuAPI.devices.getAllDevices({
					geofenceID: props.geofenceID,
					paired: true,
				}),
			).then((result) => setGeofenceDevices(result.data));
		},
		[props.geofenceID],
	);

	const geofenceAssetDevices = useMemo(() => {
		return geofenceDevices?.filter((device) => isDevicePaired(device));
	}, [geofenceDevices]);

	const {
		devices: filteredDevices,
		filterCategory,
		filterStatus,
		isFiltering,
		searchText,
		setFilterCategory,
		setFilterStatus,
		setSearchText,
		showFilterMenu,
		toggleFilterMenu,
	} = useAssetFiltering(geofenceAssetDevices);

	const {
		onSelectDevice: propsOnSelectDevice,
		onHoverDevice: propsOnHoverDevice,
	} = props;

	const filterColor = isFiltering
		? whitelabel.palette.primary900
		: whitelabel.palette.secondary700;

	return (
		<>
			<div className={styles.assetCountCard}>
				{filteredDevices === undefined ? (
					<span>{t("common.loading_devices")}</span>
				) : (
					<React.Fragment>
						<span className={styles.assetCountCardNumber}>
							{filteredDevices.length}
						</span>
						<span className={styles.assetCountCardLabel}>
							{t("dashboard.geofence_detail.assets_in_geofence")}
						</span>
					</React.Fragment>
				)}
			</div>

			<div className={styles.filterBar}>
				<VelavuInput
					className={styles.search}
					leadingIcon={
						<IconSearch
							size={16}
							color={whitelabel.palette.secondary900}
						/>
					}
					placeholder={t("common.search_assets")}
					value={searchText}
					onChange={setSearchText}
					fullWidth
				/>

				<VelavuIconButton
					highlight={isFiltering}
					onClick={toggleFilterMenu}
				>
					{showFilterMenu ? (
						<IconClose color={filterColor} />
					) : (
						<IconFilterList color={filterColor} />
					)}
				</VelavuIconButton>
			</div>

			<Divider />

			{showFilterMenu && (
				<>
					<AssetListFilter
						filterCategory={filterCategory}
						selectCategory={setFilterCategory}
						filterStatus={filterStatus}
						selectStatus={setFilterStatus}
					/>
					<Divider />
				</>
			)}

			<div className={styles.list}>
				{(filteredDevices ?? []).map((device) => (
					<AssetListItem
						key={device.asset.id}
						asset={device.asset}
						onClick={() => propsOnSelectDevice?.(device.id)}
						onMouseEnter={() => propsOnHoverDevice?.(device.id)}
						onMouseLeave={() => propsOnHoverDevice?.(undefined)}
					/>
				))}
			</div>
		</>
	);
}
